import { computed, reactive, watchEffect, type Ref, type ComputedRef, unref } from 'vue';
import type { ICustomField } from '@nova/core';
import { createCustomFieldRules } from '@satellite/helpers/customFieldValidation';

export type ValidationRule = (value: unknown) => boolean | string;

/**
 * Provides reactive validation rules for custom field components.
 * Returns rules as a reactive array (not a Ref) so template type-checking
 * works when passing to Vuetify's :rules prop.
 *
 * Rules are built from `field.required`, which is the field's own metadata. A component that
 * also accepts a `required` prop overriding that metadata must hand over the already-resolved
 * value (`{ ...props.field, required: normalizedProps.required }`), or the label and the
 * validation disagree: the control renders as optional while still refusing to validate empty.
 *
 * @param field - Custom field object (can be ref, computed, or plain object)
 * @returns Reactive validation rules and utility functions
 */
export function useCustomFieldValidation(
  field:
    | Ref<ICustomField | null | undefined>
    | ComputedRef<ICustomField | null | undefined>
    | ICustomField
    | null
    | undefined
) {
  const fieldRef = computed(() => unref(field));

  const rules = reactive<ValidationRule[]>([]);

  watchEffect(() => {
    const f = fieldRef.value;
    const newRules = !f
      ? []
      : createCustomFieldRules(f.label || 'Field', f.type, {
          required: f.required ?? false,
          minLengthOrValue: f.minLengthOrValue,
          maxLengthOrValue: f.maxLengthOrValue
        });
    rules.splice(0, rules.length, ...newRules);
  });

  const validate = (value: unknown): boolean => {
    return rules.every(rule => rule(value) === true);
  };

  const hasRules = computed(() => rules.length > 0);

  return {
    rules,
    validate,
    hasRules
  };
}
